

import { Intent, Spinner } from '@blueprintjs/core';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { QueryType } from "typegraph";

import { ApolloQueryResult } from 'apollo-client';
import autobind from 'autobind-decorator';
import * as paramsActions from 'core/actions/paramsActions';
import Label from 'core/components/label/Label';
import { IPanelProps } from 'core/decorators/Panel';
import Class from 'core/util/Class';
import Logger from 'core/util/Logger';
import { makeCancelable } from 'core/util/PromiseUtil';
import ServiceRegistry from 'core/util/ServiceRegistry';
import { iDef, nDef } from 'core/util/TypeUtils';
import { isEqual } from 'lodash';
import { QueryResult } from 'react-apollo/Query';
import IQueryService from '../service/query/IQueryService';

/**
 * @overview: Decorators to populate components with data using TypeGraph and react-apollo.
 * Some code here is unreleased code from TypeGraph (MIT). Will update when deployed.
 *
 * @author: jesposito
 */

export interface IQueryActions {
	updateParams: (params: object) => void;
	replaceParams: (params: object) => void;
}

export interface IDataWrapper<T> {
	entities: T;
}

interface IQuery {
	queryParams: any;
	queryActions: IQueryActions;
	initializing: boolean;
}

export interface IQueryOne<T> extends IQuery {
	response: QueryResult<IDataWrapper<T>>;
	entity: T;
}

type ParamsFunction =  (config: any, pageParams: any) => object;

export function QueryOne<C, T extends IQueryOne<C>>(entity: C, params?: ParamsFunction | object, useRef = false): any {
	return (Panel: IPanelProps<any> & IQueryOne<C>) => {
		return connect(mapParams, mapActions)(connectedQuery(Panel, QueryType.ONE, entity, params, useRef));
	};
}

export interface IQueryMany<T> extends IQuery {
	response: QueryResult<IDataWrapper<T[]>>;
	entities: T[];
}

export function QueryMany<C, T extends IQueryMany<C>>(entity: C, params?: ParamsFunction | object, useRef = false): any {
	return (Panel: IPanelProps<any>) => {
		return connect(mapParams, mapActions)(connectedQuery(Panel, QueryType.MANY, entity, params, useRef));
	};
}

// TODO Do not do this in render. Create wrapper component and perform in componentDidMount.
function connectedQuery(Component, type, entity, paramsFunction, useRef) {
	return (props: any) => {

		const params = typeof paramsFunction === "function" ? paramsFunction(props.config, props.pageParams) : paramsFunction;

		if(params && typeof props.queryParams === "undefined") {
			props.actions.updateParams(props.instance, params);
		}

		// Adjusted props to pass to child
		const cProps = Object.assign({}, props, {actions: undefined});

		// Bind query actions to panel instance
		cProps.queryActions = {
			updateParams: (pa) => {
				props.actions.updateParams(props.instance, pa);
			},
			replaceParams: (pa) => {
				props.actions.replaceParams(props.instance, pa);
			}
		};

		const p = { ...props.queryParams ? props.queryParams : params };

		// replace render method
		// const render = Component.prototype.render;

		/* Component.prototype.render = () => {
			console.error("RENDIONR THIS COMPONET");
			render();
		}; */

		return (
			<div>
				<QueryWrapper params={p} entity={entity} type={type} useRef={useRef}>
					{React.createElement(Component, {...props})}
				</QueryWrapper>
			</div>
		);
	};
}

interface IQueryWrapperProps {
	type: QueryType;
	entity: Class<any>;
	params: object;
	useRef: boolean;
}

interface IQueryWrapperState {
	refParams: object;
	response: ApolloQueryResult<any>;
	ref: any;
	initializing: boolean;
}

// very rough draft of a wrapper that can call methods on child components
// todo avoid duplicate generation
class QueryWrapper extends React.Component<IQueryWrapperProps, IQueryWrapperState> {
	private ref: any;
	private promise: any;

	constructor(props: IQueryWrapperProps) {
		super(props);
		this.state = {refParams: null, response: undefined, ref: undefined, initializing: false};
	}

	@autobind
	private setRef(element) {
		this.ref = element;
	}

	public shouldComponentUpdate(newProps, newState) {
		return !isEqual(newState, this.state);
	}

	public componentDidUpdate() {
		const { entity, type, params} = this.props;
		let pars: any = params;

		if(this.props.useRef && nDef(this.ref)) {
			return false;
		}

		if(iDef(this.ref) && this.ref != null && iDef(this.ref.getQueryParams)) {
			pars = {...pars.params, ...this.ref.getQueryParams()};
		}

		/* const query = generateQuery(entity, type, pars);

		Logger.debug("Generated query: %s", aliasedQuery); */

		const queryPromise = ServiceRegistry.get(IQueryService).find(type, entity, pars);

		this.promise = makeCancelable(queryPromise);

		this.promise.promise.then((response) => (
			this.setState({response, initializing: false})
		)).catch((reason) => (
			Logger.debug("Query promise canceled: " + reason)
		));
	}

	public componentDidMount() {
		const { entity, type, params} = this.props;
		const pars: any = params;

		if(nDef(this.ref)) {
			if(this.props.useRef) {
				const dummyEntity = new this.props.entity();
				this.setState({response: {data: { entities: [ dummyEntity ], entity: dummyEntity}, loading: false} as any, initializing: true});
			} else {
				this.forceUpdate();
			}
		}
	}

	public componentWillUnmount() {
		if(this.promise) {
			this.promise.cancel();
		}
	}

	public render() {
		const { type } = this.props;
		const { response, initializing } = this.state;

		if(!response || response.loading || nDef(response.data) || ( (nDef(response.data.entities)) && (nDef(response.data.entity)))) {
			return <Spinner className="pt-large" intent={Intent.PRIMARY} />;
		}

		if(response.data.entities == null && response.data.entity == null) {
			if (QueryType.ONE === type) {
				return <Label k="not.found" v="Item Not Found" />;
			}
			return <Label k="no.results" v="No Results" />;
		}

		const iQuery = { response };

		if(QueryType.MANY === type) {

			return React.cloneElement(React.Children.only(this.props.children) as any, {...iQuery, initializing, entities: response.data.entities, ref: this.setRef });

		} else if(QueryType.ONE === type) {

			return React.cloneElement(React.Children.only(this.props.children) as any, {...iQuery, initializing, entity: response.data.entity, ref: this.setRef });

		}
	}
}

const mapParams = (state, ownProps: any): any => {
	return {
		queryParams: state.params.panel[ownProps.instance]
	};
};

const mapActions = (dispatch): any => {
	return {
		actions: bindActionCreators<any>(paramsActions, dispatch)
	};
};



// WEBPACK FOOTER //
// ./src/core/api/query.tsx